import { AppState } from "store/rootReducer";
import { createSelector } from "reselect";
import { getExceptions } from "store/features/emailScheduler/definition/definition.selectors";

const emailSchedulerOverviewState = (state: AppState) => state.emailScheduler.overview;

export const getSchedulerOverviewProcessingState = createSelector(
    emailSchedulerOverviewState,
    ({ isFetching, isDeleting, hasError }) => ({ isFetching, isDeleting, hasError })
);

export const getRuleDetailsById = (id?: string) =>
    createSelector(emailSchedulerOverviewState, ({ configurations }) => {
        return configurations.find(({ general }) => general.id === id);
    });

export const getSortableRules = createSelector(emailSchedulerOverviewState, ({ configurations }) => {
    return configurations.map(({ general, filters }) => {
        return {
            id: general.id,
            active: general.active,
            name: general.name,
            prodCodes: filters.map(({ translation }) => translation),
        };
    });
});

export const getRuleExceptions = createSelector(emailSchedulerOverviewState, ({ configurations }) =>
    configurations.map(({ general }) => ({ ruleName: general.name, exceptions: general.exceptions }))
);

export const getSortableRulesRelatedExceptions = createSelector(
    getRuleExceptions,
    getExceptions,
    getSchedulerOverviewProcessingState,
    (ruleExceptions, exceptions, { isFetching }) => {
        // don't populate until exception won't be populated
        if (isFetching) {
            return [];
        }

        const relatedExceptions = exceptions.map(({ name }) => ({ exceptionName: name, rules: [] as string[] }));

        return ruleExceptions.reduce((reducedExceptions, rule) => {
            const containsRelatedExceptions = reducedExceptions.some(({ exceptionName }) =>
                rule.exceptions.some((exception) => exception.name === exceptionName && exception.isChecked)
            );

            // if rule has related exceptions
            if (containsRelatedExceptions) {
                const ruleAlreadyExists = reducedExceptions.some(({ rules }) =>
                    rules.some((relatedRule) => relatedRule === rule.ruleName)
                );

                // if rule already exists
                if (ruleAlreadyExists) {
                    return reducedExceptions;
                }

                return reducedExceptions.map((reducedException) => {
                    const hasRelatedException = rule.exceptions.some(
                        ({ isChecked, name }) => isChecked && name === reducedException.exceptionName
                    );

                    // if exception is checked inside rule
                    if (hasRelatedException) {
                        return {
                            ...reducedException,
                            rules: [...reducedException.rules, rule.ruleName],
                        };
                    }

                    return reducedException;
                });
            }

            return reducedExceptions;
        }, relatedExceptions);
    }
);
